/**
 * Strings for the Appearance studio (pages/AppearancePage.tsx,
 * components/appearance/*). Spread into the main catalog; `APPEARANCE_EN` is
 * typed against `APPEARANCE_ES`, so a missing English string fails to compile.
 */

export const APPEARANCE_ES = {
  'appearance.title': 'Apariencia',
  'appearance.subtitle': 'Elige un tema o crea el tuyo. Tus temas se guardan en tu Drive y llegan a tus otros dispositivos; los modos claro y nocturno y el movimiento son de este dispositivo.',
  'appearance.editorLabel': 'Editar el tema',

  // Gallery
  'appearance.gallery.label': 'Temas',
  'appearance.gallery.included': 'Incluido',
  'appearance.gallery.yours': 'Tuyo',
  'appearance.gallery.editing': 'Editando una copia',
  'appearance.gallery.new': 'Nuevo tema',
  'appearance.gallery.newHint': 'Copia del tema actual',
  'appearance.gallery.limit': 'Tienes {max} temas, el máximo',
  'appearance.gallery.created': 'Tema creado. Los cambios se guardan solos.',

  // Editor bar
  'appearance.preset.body': '«{name}» es un tema incluido. Si cambias algo preparamos una copia que podrás guardar con tu nombre.',
  'appearance.draft.title': 'Cambios sin guardar',
  'appearance.draft.body': 'sobre «{name}». Ponle nombre y guárdalo como tema tuyo.',
  'appearance.draft.nameLabel': 'Nombre del tema',
  'appearance.draft.defaultName': 'Mi {name}',
  'appearance.draft.save': 'Guardar tema',
  'appearance.draft.saved': 'Tema guardado',
  'appearance.draft.discarded': 'Se descartaron los cambios sin guardar',
  'appearance.draft.limit': 'Ya tienes {max} temas, el máximo. Borra uno para guardar este.',
  'appearance.custom.saved': 'Se guarda solo y llega a tus otros dispositivos.',
  'appearance.rename': 'Nombre del tema',
  'appearance.delete.title': '¿Borrar «{name}»?',
  'appearance.delete.body': 'Se borra en todos tus dispositivos. Los temas incluidos no se pueden borrar.',
  'appearance.resetSection': 'Restablecer sección',

  // Tabs
  'appearance.tab.colors': 'Colores',
  'appearance.tab.type': 'Tipografía',
  'appearance.tab.card': 'Tarjeta',
  'appearance.tab.background': 'Fondo',
  'appearance.tab.device': 'En este dispositivo',

  // Colours
  'appearance.colors.intro': 'Bordes, superficies secundarias y texto atenuado se derivan de estos cinco colores, siempre legibles.',
  'appearance.colors.bg': 'Fondo',
  'appearance.colors.bgHint': 'El color detrás de todo.',
  'appearance.colors.surface': 'Superficie',
  'appearance.colors.surfaceHint': 'Paneles, tarjetas y menús.',
  'appearance.colors.title': 'Títulos',
  'appearance.colors.body': 'Texto',
  'appearance.colors.bodyHint': 'El texto secundario se deriva de este.',
  'appearance.colors.accent': 'Acento',
  'appearance.colors.accentHint': 'Botones, enlaces y selección. Como texto se ajusta solo para leerse.',
  'appearance.pickColor': 'Elegir color: {name}',
  'appearance.suggested': 'Colores sugeridos',
  'appearance.contrast.good': 'AA',
  'appearance.contrast.fair': 'Grande',
  'appearance.contrast.poor': 'Bajo',
  'appearance.contrast.goodHint': 'Contraste suficiente para cualquier texto (WCAG AA, 4,5:1).',
  'appearance.contrast.fairHint': 'Solo para texto grande o elementos de interfaz (3:1).',
  'appearance.contrast.poorHint': 'Por debajo de 3:1: difícil de leer.',

  // Type
  'appearance.type.intro': 'Cada opción se muestra en su propia letra. Las del sistema pueden no existir en otros dispositivos.',
  'appearance.type.title': 'Títulos',
  'appearance.type.titleHint': 'Encabezados de página y de nota.',
  'appearance.type.body': 'Texto',
  'appearance.type.bodyHint': 'Interfaz y cuerpo de las notas.',
  'appearance.type.size': 'Tamaño base',
  'appearance.type.sizeHint': 'Escala todo el texto de la app.',
  'appearance.font.group.bundled': 'Incluidas en QUIPWA',
  'appearance.font.group.serif': 'Con serifa',
  'appearance.font.group.sans': 'Sin serifa',
  'appearance.font.group.mono': 'Monoespaciadas',
  'appearance.font.missing': 'No está en este dispositivo',

  // Card
  'appearance.card.intro': 'Cómo se ve la tarjeta durante el repaso.',
  'appearance.card.font': 'Letra de la tarjeta',
  'appearance.card.size': 'Tamaño del texto',
  'appearance.card.radius': 'Esquinas',
  'appearance.card.shadow': 'Sombra',
  'appearance.card.flip': 'Al voltear',
  'appearance.card.flipHint': 'Con «Reducir movimiento» activo, cualquier animación se acorta.',
  'appearance.card.tryFlip': 'Probar',
  'appearance.shadow.none': 'Ninguna',
  'appearance.shadow.soft': 'Suave',
  'appearance.shadow.deep': 'Profunda',
  'appearance.shadow.theme': 'La del tema',
  'appearance.flip.h': 'Giro',
  'appearance.flip.v': 'Giro vertical',
  'appearance.flip.fade': 'Fundido',
  'appearance.flip.slide': 'Deslizar',
  'appearance.flip.instant': 'Sin animación',

  // Background
  'appearance.bg.intro': 'La capa que se ve detrás de toda la app.',
  'appearance.bg.type': 'Tipo',
  'appearance.bg.solid': 'Color',
  'appearance.bg.pattern': 'Patrón',
  'appearance.bg.image': 'Imagen',
  'appearance.bg.patternStyle': 'Patrón',
  'appearance.pattern.dots': 'Puntos',
  'appearance.pattern.grid': 'Cuadrícula',
  'appearance.pattern.lines': 'Líneas',
  'appearance.pattern.splatter': 'Salpicado',
  'appearance.bg.strength': 'Intensidad',
  'appearance.bg.blur': 'Desenfoque',
  'appearance.bg.imageFile': 'Imagen',
  'appearance.bg.imageHint': 'Se guarda en tu Drive, en QUIPWA/assets. Máximo {max} MB.',
  'appearance.bg.imageNeedsDrive': 'Para subir imágenes hace falta conectar Google Drive (Ajustes → Cuenta).',
  'appearance.bg.chooseImage': 'Elegir imagen',
  'appearance.bg.replaceImage': 'Cambiar imagen',
  'appearance.bg.colorWhere': 'El color del fondo se elige en Colores → Fondo.',
  'appearance.uploading': 'Subiendo…',

  // Device
  'appearance.device.intro': 'Preferencias de este dispositivo: no forman parte del tema ni se sincronizan.',
  'appearance.night.mode': 'Modo nocturno',
  'appearance.night.hint': 'Oscurece y calienta los colores del tema. Valoraciones, colores de mazo y estados se mantienen.',
  'appearance.night.pausedByLight': 'En pausa mientras el modo claro esté activo.',
  'appearance.light.label': 'Modo claro',
  'appearance.light.hint': 'Una versión clara del tema activo, con su mismo acento. No cambia el tema y tiene prioridad sobre el modo nocturno.',
  'appearance.light.notice': 'Modo claro activo: la app muestra una versión clara de este tema. Lo que edites aquí es el tema original.',
  'theme.light.on': 'Forzar modo claro',
  'theme.light.off': 'Quitar modo claro',
  'theme.light.alreadyLight': 'El tema activo ya es claro',
  'appearance.night.off': 'Desactivado',
  'appearance.night.on': 'Activado',
  'appearance.night.schedule': 'Con horario',
  'appearance.night.from': 'De',
  'appearance.night.to': 'a',
  'appearance.motion.label': 'Animaciones',
  'appearance.motion.system': 'Como el sistema',
  'appearance.motion.reduced': 'Reducidas',
  'appearance.motion.full': 'Completas',
  'appearance.motion.hint': 'Se aplica a toda la app en este dispositivo.',
  'appearance.skin.label': 'Piel',
  'appearance.skin.hint': 'El aspecto de los controles. Fichero los dibuja como un aparato de los 2000: teclas con relieve, pantallas LCD, fichas de cartulina. Los colores siguen siendo los del tema.',
  'appearance.skin.plana': 'Plana',
  'appearance.skin.fichero': 'Fichero',
  'appearance.thickness.label': 'Grosor',
  'appearance.thickness.hint': 'Cuánto relieve tienen las teclas y las placas. No cambia el tamaño del texto ni el de las zonas que se tocan.',
  'appearance.thickness.sutil': 'Sutil',
  'appearance.thickness.clasico': 'Clásico',
  'appearance.thickness.tosco': 'Tosco',
  'appearance.sound.label': 'Sonido',
  'appearance.sound.hint': 'Viene apagado. Encendido, suenan clics de aparato al pulsar las teclas, sellar o voltear una ficha, y la papeleta al terminar. Nunca con el micrófono abierto ni mientras la voz lee. Sigue el volumen del dispositivo y, en el iPhone, el modo silencio.',
  'appearance.sound.toggle': 'Sonido',
  'appearance.sound.volume': 'Volumen del sonido',
  'appearance.sound.tip': 'Fichero puede sonar: actívalo en Sonido.',
  'appearance.sound.tipAction': 'Activar',
  'appearance.sound.turnedOn': 'Sonido de Fichero activado',
  'appearance.sound.turnedOff': 'Sonido de Fichero desactivado',
  'appearance.motion.systemReduced': 'Tu sistema pide menos movimiento, así que las animaciones están reducidas.',
  'appearance.motion.systemFull': 'Tu sistema no pide reducir el movimiento.',
  'appearance.celebration.label': 'Celebración al terminar una sesión',
  'appearance.celebration.hint': 'El confeti se omite si las animaciones están reducidas.',
  'appearance.celebration.none': 'Nada',
  'appearance.celebration.confetti': 'Confeti',
  'appearance.celebration.image': 'Una imagen tuya',
  'appearance.celebration.try': 'Probar',
  'appearance.celebration.imageSaved': 'Imagen guardada',
  'appearance.celebration.imageUnavailable': 'No se pudo cargar la imagen desde tu Drive.',

  // Preview
  'appearance.preview.title': 'Vista previa',
  'appearance.preview.showBack': 'Ver respuesta',
  'appearance.preview.showFront': 'Ver pregunta',
  'appearance.preview.deck': 'Biología',
  'appearance.preview.cardFront': '¿Qué orgánulo produce la mayor parte del ATP de la célula?',
  'appearance.preview.cardBack': 'La mitocondria, mediante la respiración celular.',
  'appearance.preview.noteTitle': 'Mitocondria',
  'appearance.preview.noteBodyStart': 'Produce',
  'appearance.preview.noteBodyEnd': 'a partir de glucosa y oxígeno. Ver también',
  'appearance.preview.noteLink': 'respiración celular',
  'appearance.preview.noteMeta': 'editada hace 2 días',
  'appearance.preview.primary': 'Repasar ahora',
  'appearance.preview.secondary': 'Más tarde',
  'appearance.preview.imageNote': 'La imagen de fondo se ve detrás de toda la app, no en esta muestra.',
} as const

export const APPEARANCE_EN: Record<keyof typeof APPEARANCE_ES, string> = {
  'appearance.title': 'Appearance',
  'appearance.subtitle': 'Pick a theme or make your own. Your themes are saved to your Drive and reach your other devices; light and night mode and motion belong to this device.',
  'appearance.editorLabel': 'Edit the theme',

  'appearance.gallery.label': 'Themes',
  'appearance.gallery.included': 'Included',
  'appearance.gallery.yours': 'Yours',
  'appearance.gallery.editing': 'Editing a copy',
  'appearance.gallery.new': 'New theme',
  'appearance.gallery.newHint': 'A copy of the current theme',
  'appearance.gallery.limit': 'You have {max} themes, the maximum',
  'appearance.gallery.created': 'Theme created. Changes save on their own.',

  'appearance.preset.body': '“{name}” is an included theme. If you change anything we prepare a copy you can save under your own name.',
  'appearance.draft.title': 'Unsaved changes',
  'appearance.draft.body': 'to “{name}”. Name it and save it as your own theme.',
  'appearance.draft.nameLabel': 'Theme name',
  'appearance.draft.defaultName': 'My {name}',
  'appearance.draft.save': 'Save theme',
  'appearance.draft.saved': 'Theme saved',
  'appearance.draft.discarded': 'Unsaved changes were discarded',
  'appearance.draft.limit': 'You already have {max} themes, the maximum. Delete one to save this.',
  'appearance.custom.saved': 'Saves on its own and reaches your other devices.',
  'appearance.rename': 'Theme name',
  'appearance.delete.title': 'Delete “{name}”?',
  'appearance.delete.body': 'It is deleted on all your devices. Included themes cannot be deleted.',
  'appearance.resetSection': 'Reset section',

  'appearance.tab.colors': 'Colours',
  'appearance.tab.type': 'Type',
  'appearance.tab.card': 'Card',
  'appearance.tab.background': 'Background',
  'appearance.tab.device': 'On this device',

  'appearance.colors.intro': 'Borders, secondary surfaces and muted text are derived from these five colours, always legible.',
  'appearance.colors.bg': 'Background',
  'appearance.colors.bgHint': 'The colour behind everything.',
  'appearance.colors.surface': 'Surface',
  'appearance.colors.surfaceHint': 'Panels, cards and menus.',
  'appearance.colors.title': 'Headings',
  'appearance.colors.body': 'Text',
  'appearance.colors.bodyHint': 'Secondary text is derived from this one.',
  'appearance.colors.accent': 'Accent',
  'appearance.colors.accentHint': 'Buttons, links and selection. As text it adjusts itself to stay readable.',
  'appearance.pickColor': 'Pick colour: {name}',
  'appearance.suggested': 'Suggested colours',
  'appearance.contrast.good': 'AA',
  'appearance.contrast.fair': 'Large',
  'appearance.contrast.poor': 'Low',
  'appearance.contrast.goodHint': 'Enough contrast for any text (WCAG AA, 4.5:1).',
  'appearance.contrast.fairHint': 'Only for large text or interface elements (3:1).',
  'appearance.contrast.poorHint': 'Below 3:1: hard to read.',

  'appearance.type.intro': 'Each option is shown in its own typeface. System fonts may not exist on other devices.',
  'appearance.type.title': 'Headings',
  'appearance.type.titleHint': 'Page and note headings.',
  'appearance.type.body': 'Text',
  'appearance.type.bodyHint': 'Interface and note body.',
  'appearance.type.size': 'Base size',
  'appearance.type.sizeHint': 'Scales all the text in the app.',
  'appearance.font.group.bundled': 'Included in QUIPWA',
  'appearance.font.group.serif': 'Serif',
  'appearance.font.group.sans': 'Sans serif',
  'appearance.font.group.mono': 'Monospaced',
  'appearance.font.missing': 'Not on this device',

  'appearance.card.intro': 'How the card looks during review.',
  'appearance.card.font': 'Card typeface',
  'appearance.card.size': 'Text size',
  'appearance.card.radius': 'Corners',
  'appearance.card.shadow': 'Shadow',
  'appearance.card.flip': 'Flip',
  'appearance.card.flipHint': 'With “Reduce motion” on, any animation is shortened.',
  'appearance.card.tryFlip': 'Try it',
  'appearance.shadow.none': 'None',
  'appearance.shadow.soft': 'Soft',
  'appearance.shadow.deep': 'Deep',
  'appearance.shadow.theme': 'The theme’s',
  'appearance.flip.h': 'Turn',
  'appearance.flip.v': 'Vertical turn',
  'appearance.flip.fade': 'Fade',
  'appearance.flip.slide': 'Slide',
  'appearance.flip.instant': 'No animation',

  'appearance.bg.intro': 'The layer behind the whole app.',
  'appearance.bg.type': 'Type',
  'appearance.bg.solid': 'Colour',
  'appearance.bg.pattern': 'Pattern',
  'appearance.bg.image': 'Image',
  'appearance.bg.patternStyle': 'Pattern',
  'appearance.pattern.dots': 'Dots',
  'appearance.pattern.grid': 'Grid',
  'appearance.pattern.lines': 'Lines',
  'appearance.pattern.splatter': 'Splatter',
  'appearance.bg.strength': 'Strength',
  'appearance.bg.blur': 'Blur',
  'appearance.bg.imageFile': 'Image',
  'appearance.bg.imageHint': 'Saved to your Drive, in QUIPWA/assets. Max {max} MB.',
  'appearance.bg.imageNeedsDrive': 'Uploading images needs Google Drive connected (Settings → Account).',
  'appearance.bg.chooseImage': 'Choose image',
  'appearance.bg.replaceImage': 'Change image',
  'appearance.bg.colorWhere': 'The background colour is set in Colours → Background.',
  'appearance.uploading': 'Uploading…',

  'appearance.device.intro': 'Preferences for this device: not part of the theme and not synced.',
  'appearance.night.mode': 'Night mode',
  'appearance.night.hint': 'Darkens and warms the theme’s colours. Ratings, deck colours and states are kept.',
  'appearance.night.pausedByLight': 'Paused while light mode is on.',
  'appearance.light.label': 'Light mode',
  'appearance.light.hint': 'A light version of the active theme, with the same accent. It does not change the theme and wins over night mode.',
  'appearance.light.notice': 'Light mode is on: the app shows a light version of this theme. What you edit here is the original theme.',
  'theme.light.on': 'Force light mode',
  'theme.light.off': 'Turn off light mode',
  'theme.light.alreadyLight': 'The active theme is already light',
  'appearance.night.off': 'Off',
  'appearance.night.on': 'On',
  'appearance.night.schedule': 'Scheduled',
  'appearance.night.from': 'From',
  'appearance.night.to': 'to',
  'appearance.motion.label': 'Animations',
  'appearance.motion.system': 'Like the system',
  'appearance.motion.reduced': 'Reduced',
  'appearance.motion.full': 'Full',
  'appearance.motion.hint': 'Applies to the whole app on this device.',
  'appearance.skin.label': 'Skin',
  'appearance.skin.hint': 'How the controls look. Fichero draws them like a device from the 2000s: raised keys, LCD displays, index cards. The colours are still the theme’s.',
  'appearance.skin.plana': 'Flat',
  'appearance.skin.fichero': 'Fichero',
  'appearance.thickness.label': 'Thickness',
  'appearance.thickness.hint': 'How much relief keys and plates have. Text size and touch areas stay the same.',
  'appearance.thickness.sutil': 'Subtle',
  'appearance.thickness.clasico': 'Classic',
  'appearance.thickness.tosco': 'Chunky',
  'appearance.sound.label': 'Sound',
  'appearance.sound.hint': 'Off until you turn it on. When on: device clicks when you press keys, stamp or flip a card, and the receipt at the end. Never while the microphone is open or the voice is reading. Follows the device volume and, on iPhone, silent mode.',
  'appearance.sound.toggle': 'Sound',
  'appearance.sound.volume': 'Sound volume',
  'appearance.sound.tip': 'Fichero can make sounds: turn them on under Sound.',
  'appearance.sound.tipAction': 'Turn on',
  'appearance.sound.turnedOn': 'Fichero sound on',
  'appearance.sound.turnedOff': 'Fichero sound off',
  'appearance.motion.systemReduced': 'Your system asks for less motion, so animations are reduced.',
  'appearance.motion.systemFull': 'Your system does not ask to reduce motion.',
  'appearance.celebration.label': 'Celebration when a session ends',
  'appearance.celebration.hint': 'Confetti is skipped when animations are reduced.',
  'appearance.celebration.none': 'Nothing',
  'appearance.celebration.confetti': 'Confetti',
  'appearance.celebration.image': 'An image of yours',
  'appearance.celebration.try': 'Try it',
  'appearance.celebration.imageSaved': 'Image saved',
  'appearance.celebration.imageUnavailable': 'The image could not be loaded from your Drive.',

  'appearance.preview.title': 'Preview',
  'appearance.preview.showBack': 'Show answer',
  'appearance.preview.showFront': 'Show question',
  'appearance.preview.deck': 'Biology',
  'appearance.preview.cardFront': 'Which organelle makes most of the cell’s ATP?',
  'appearance.preview.cardBack': 'The mitochondrion, through cellular respiration.',
  'appearance.preview.noteTitle': 'Mitochondrion',
  'appearance.preview.noteBodyStart': 'Makes',
  'appearance.preview.noteBodyEnd': 'from glucose and oxygen. See also',
  'appearance.preview.noteLink': 'cellular respiration',
  'appearance.preview.noteMeta': 'edited 2 days ago',
  'appearance.preview.primary': 'Review now',
  'appearance.preview.secondary': 'Later',
  'appearance.preview.imageNote': 'The background image shows behind the whole app, not in this sample.',
}
